import React, { useState, useEffect } from 'react';
import { Share, ChevronDown, HelpCircle, CreditCard, Briefcase, Home, Building } from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatPercentage = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value / 100);
};

const EligibilityCalculator = () => {
  // State for basic inputs
  const [monthlyIncome, setMonthlyIncome] = useState(80000);
  const [monthlyExpenses, setMonthlyExpenses] = useState(30000);
  const [existingEMIs, setExistingEMIs] = useState(15000);
  const [loanType, setLoanType] = useState('personal');
  
  // State for loan details
  const [interestRate, setInterestRate] = useState(10);
  const [loanTenure, setLoanTenure] = useState(5);
  
  // State for employment details
  const [employmentType, setEmploymentType] = useState('salaried');
  const [creditScore, setCreditScore] = useState(750);
  
  // State for advanced settings
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [ageYears, setAgeYears] = useState(35);
  const [annualBonusIncome, setAnnualBonusIncome] = useState(100000);
  const [propertyValue, setPropertyValue] = useState(5000000);
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);
  
  // State for share toast
  const [showShareToast, setShowShareToast] = useState(false);
  
  // State for tooltips
  const [showToolTip, setShowToolTip] = useState('');
  
  // State for calculation results
  const [result, setResult] = useState({
    eligibleLoanAmount: 0,
    maxMonthlyEMI: 0,
    loanToValueRatio: 0,
    debtToIncomeRatio: 0,
    requiredDownPayment: 0,
    annualInterestPayment: 0,
    totalInterestPayment: 0,
    actualLoanAmount: 0
  });

  // Calculate loan eligibility
  const calculateLoanEligibility = () => {
    // Calculate disposable income
    const disposableIncome = monthlyIncome - monthlyExpenses;
    
    // Calculate Debt-to-Income ratio (including existing EMIs)
    const debtToIncomeRatio = ((existingEMIs) / monthlyIncome) * 100;
    
    // Maximum allowed EMI (50% of disposable income for safety)
    const maxEMIAllowed = Math.max(0, disposableIncome * 0.5);
    
    // Adjust max EMI based on credit score
    let creditScoreFactor = 1.0;
    if (creditScore >= 800) {
      creditScoreFactor = 1.1; // 10% bonus for excellent credit
    } else if (creditScore >= 750) {
      creditScoreFactor = 1.0; // Normal factor for good credit
    } else if (creditScore >= 650) {
      creditScoreFactor = 0.9; // 10% reduction for fair credit
    } else {
      creditScoreFactor = 0.7; // 30% reduction for poor credit
    }
    
    // Adjust for employment type
    let employmentFactor = 1.0;
    if (employmentType === 'salaried') {
      employmentFactor = 1.0;
    } else if (employmentType === 'self-employed') {
      employmentFactor = 0.9; // Slight reduction for self-employed
    } else if (employmentType === 'business-owner') {
      employmentFactor = 0.95; // Business owners are considered between salaried and self-employed
    }
    
    // Factor in annual bonus (divided by 12 for monthly impact)
    const effectiveMonthlyIncome = monthlyIncome + (annualBonusIncome / 12);
    
    // Adjust max EMI based on age (inversely proportional to age)
    let ageFactor = 1.0;
    if (ageYears <= 30) {
      ageFactor = 1.0; // Full eligibility for younger applicants
    } else if (ageYears <= 45) {
      ageFactor = 0.9; // Slight reduction for middle-aged
    } else if (ageYears <= 55) {
      ageFactor = 0.8; // Further reduction for older applicants
    } else {
      ageFactor = 0.7; // Significant reduction for near-retirement age
    }
    
    // Final maximum EMI
    const maxMonthlyEMI = Math.max(0, (maxEMIAllowed - existingEMIs) * creditScoreFactor * employmentFactor * ageFactor);
    
    // Convert this to loan amount using the loan formula: P = E * [1 - (1 + r)^-n] / r
    // Where P is principal, E is EMI, r is monthly interest rate, n is number of months
    const monthlyInterestRate = (interestRate / 12) / 100;
    const numberOfMonths = loanTenure * 12;
    
    const eligibleLoanAmount = maxMonthlyEMI * (1 - Math.pow(1 + monthlyInterestRate, -numberOfMonths)) / monthlyInterestRate;
    
    // For home loans, consider property value and down payment
    let actualLoanAmount = eligibleLoanAmount;
    let requiredDownPayment = 0;
    let loanToValueRatio = 0;
    
    if (loanType === 'home') {
      // Maximum LTV ratio (usually 80% for home loans)
      const maxLTV = 80;
      const calculatedDownPayment = propertyValue * (downPaymentPercent / 100);
      const maxLoanBasedOnLTV = propertyValue * (maxLTV / 100);
      
      // The actual loan amount is the minimum of eligible amount and max based on LTV
      actualLoanAmount = Math.min(eligibleLoanAmount, maxLoanBasedOnLTV);
      
      // Required down payment
      requiredDownPayment = propertyValue - actualLoanAmount;
      
      // Calculate actual LTV ratio
      loanToValueRatio = (actualLoanAmount / propertyValue) * 100;
    }
    
    // Calculate interest payments
    const annualInterestPayment = actualLoanAmount * (interestRate / 100);
    const totalInterestPayment = (maxMonthlyEMI * numberOfMonths) - actualLoanAmount;
    
    return {
      eligibleLoanAmount: Math.round(eligibleLoanAmount),
      maxMonthlyEMI: Math.round(maxMonthlyEMI),
      loanToValueRatio: loanToValueRatio,
      debtToIncomeRatio: debtToIncomeRatio,
      requiredDownPayment: Math.round(requiredDownPayment),
      annualInterestPayment: Math.round(annualInterestPayment),
      totalInterestPayment: Math.round(totalInterestPayment),
      actualLoanAmount: Math.round(actualLoanAmount)
    };
  };

  // Recalculate whenever inputs change
  useEffect(() => {
    setResult(calculateLoanEligibility());
  }, [
    monthlyIncome,
    monthlyExpenses,
    existingEMIs,
    loanType,
    interestRate,
    loanTenure,
    employmentType,
    creditScore,
    ageYears,
    annualBonusIncome,
    propertyValue,
    downPaymentPercent
  ]);

  // Handle share button click
  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <div className="calculator-container pt-24">
      <div className="calculator-header text-center mb-8">
        <h1 className="text-2xl font-semibold text-[#113262] mb-2">Loan Eligibility Calculator</h1>
        <h2 className="text-lg text-gray-600">Estimate how much loan you may qualify for based on your financial profile</h2>
      </div>

      <div className="max-w-5xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Sections */}
          <div className="lg:col-span-2 space-y-8">
            {/* Income & Expenses */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Income & Expenses</h2>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Monthly Income
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('monthlyIncome')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'monthlyIncome' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Your total monthly income from all sources (salary, rent, investments, etc.)
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={monthlyIncome}
                    onChange={(e) => setMonthlyIncome(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={10000}
                    max={1000000}
                    step={5000}
                    value={monthlyIncome}
                    onChange={(e) => setMonthlyIncome(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹10K</span>
                    <span>₹10L</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Monthly Expenses
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('monthlyExpenses')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'monthlyExpenses' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Your average monthly expenses excluding loan EMIs
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={monthlyExpenses}
                    onChange={(e) => setMonthlyExpenses(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={5000}
                    max={500000}
                    step={1000}
                    value={monthlyExpenses}
                    onChange={(e) => setMonthlyExpenses(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹5K</span>
                    <span>₹5L</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Existing EMIs
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('existingEMIs')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'existingEMIs' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Total of all your current loan EMIs per month
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={existingEMIs}
                    onChange={(e) => setExistingEMIs(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={0}
                    max={200000}
                    step={1000}
                    value={existingEMIs}
                    onChange={(e) => setExistingEMIs(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹0</span>
                    <span>₹2L</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Loan Details */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Loan Details</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Interest Rate
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('interestRate')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'interestRate' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Annual interest rate for the loan
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={4}
                    max={24}
                    step={0.1}
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>4%</span>
                    <span>24%</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5">Loan Tenure (Years)</label>
                  <input
                    type="number"
                    value={loanTenure}
                    onChange={(e) => setLoanTenure(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1}
                    max={30}
                    step={1}
                    value={loanTenure}
                    onChange={(e) => setLoanTenure(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1 year</span>
                    <span>30 years</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Loan Type */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Loan Type & Employment</h2>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-3">Loan Type</label>
                  <div className="grid grid-cols-3 gap-2">
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        loanType === 'personal' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setLoanType('personal')}
                    >
                      <div className="flex justify-center mb-1">
                        <CreditCard size={20} className="text-gray-600" />
                      </div>
                      <p className="font-medium">Personal Loan</p>
                      <p className="text-xs text-gray-500 mt-1">No collateral needed</p>
                    </div>
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        loanType === 'home' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setLoanType('home')}
                    >
                      <div className="flex justify-center mb-1">
                        <Home size={20} className="text-gray-600" />
                      </div>
                      <p className="font-medium">Home Loan</p>
                      <p className="text-xs text-gray-500 mt-1">Property as collateral</p>
                    </div>
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        loanType === 'business' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setLoanType('business')}
                    >
                      <div className="flex justify-center mb-1">
                        <Building size={20} className="text-gray-600" />
                      </div>
                      <p className="font-medium">Business Loan</p>
                      <p className="text-xs text-gray-500 mt-1">For business expansion</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-3">Employment Type</label>
                  <div className="grid grid-cols-3 gap-2">
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        employmentType === 'salaried' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setEmploymentType('salaried')}
                    >
                      <p className="font-medium">Salaried</p>
                      <p className="text-xs text-gray-500 mt-1">Regular fixed income</p>
                    </div>
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        employmentType === 'self-employed' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setEmploymentType('self-employed')}
                    >
                      <p className="font-medium">Self-Employed</p>
                      <p className="text-xs text-gray-500 mt-1">Professional practice</p>
                    </div>
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        employmentType === 'business-owner' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setEmploymentType('business-owner')}
                    >
                      <p className="font-medium">Business Owner</p>
                      <p className="text-xs text-gray-500 mt-1">Own a business</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Credit Score
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('creditScore')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'creditScore' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Your CIBIL or other credit bureau score (300-900)
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={creditScore}
                    onChange={(e) => setCreditScore(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={300}
                    max={900}
                    step={10}
                    value={creditScore}
                    onChange={(e) => setCreditScore(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>300 (Poor)</span>
                    <span>900 (Excellent)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="bg-white rounded-lg shadow">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-50"
              >
                <h2 className="text-lg font-bold text-gray-900">Advanced Settings</h2>
                <ChevronDown 
                  className={`transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  size={20}
                />
              </button>
              
              {showAdvanced && (
                <div className="p-4 border-t">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block font-medium text-sm mb-1.5">Age (Years)</label>
                      <input
                        type="number"
                        value={ageYears}
                        onChange={(e) => setAgeYears(Number(e.target.value))}
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={21}
                        max={70}
                        step={1}
                        value={ageYears}
                        onChange={(e) => setAgeYears(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>21 years</span>
                        <span>70 years</span>
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium text-sm mb-1.5">Annual Bonus/Additional Income</label>
                      <input
                        type="number"
                        value={annualBonusIncome}
                        onChange={(e) => setAnnualBonusIncome(Number(e.target.value))}
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={0}
                        max={1000000}
                        step={10000}
                        value={annualBonusIncome}
                        onChange={(e) => setAnnualBonusIncome(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>₹0</span>
                        <span>₹10L</span>
                      </div>
                    </div>

                    {loanType === 'home' && (
                      <>
                        <div>
                          <label className="block font-medium text-sm mb-1.5">Property Value</label>
                          <input
                            type="number"
                            value={propertyValue}
                            onChange={(e) => setPropertyValue(Number(e.target.value))}
                            className="w-full p-1.5 border rounded text-sm"
                          />
                          <input
                            type="range"
                            min={1000000}
                            max={50000000}
                            step={100000}
                            value={propertyValue}
                            onChange={(e) => setPropertyValue(Number(e.target.value))}
                            className="w-full mt-2"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>₹10L</span>
                            <span>₹5Cr</span>
                          </div>
                        </div>

                        <div>
                          <label className="block font-medium text-sm mb-1.5">Down Payment (%)</label>
                          <input
                            type="number"
                            value={downPaymentPercent}
                            onChange={(e) => setDownPaymentPercent(Number(e.target.value))}
                            className="w-full p-1.5 border rounded text-sm"
                          />
                          <input
                            type="range"
                            min={10}
                            max={50}
                            step={1}
                            value={downPaymentPercent}
                            onChange={(e) => setDownPaymentPercent(Number(e.target.value))}
                            className="w-full mt-2"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>10%</span>
                            <span>50%</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-[#113262] text-white rounded-lg h-[500px] sticky top-6">
            <div className="p-4 border-b border-white/20">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">Loan Eligibility</h3>
                <button onClick={handleShare} className="p-1 hover:bg-blue-700 rounded">
                  <Share size={18} />
                </button>
              </div>
            </div>

            <div className="p-4 flex flex-col h-[calc(100%-68px)] justify-between">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">
                    {formatCurrency(loanType === 'home' ? result.actualLoanAmount : result.eligibleLoanAmount)}
                  </div>
                  <div className="text-sm text-gray-300">Eligible Loan Amount</div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Maximum Monthly EMI</span>
                    <span className="font-bold">{formatCurrency(result.maxMonthlyEMI)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Current Debt-to-Income Ratio</span>
                    <span className="font-bold">{result.debtToIncomeRatio.toFixed(1)}%</span>
                  </div>

                  {loanType === 'home' && (
                    <>
                      <div className="flex justify-between items-center py-2 border-t border-white/20">
                        <span className="text-sm">Required Down Payment</span>
                        <span className="font-bold">{formatCurrency(result.requiredDownPayment)}</span>
                      </div>

                      <div className="flex justify-between items-center py-2 border-t border-white/20">
                        <span className="text-sm">Loan-to-Value Ratio</span>
                        <span className="font-bold">{result.loanToValueRatio.toFixed(1)}%</span>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Annual Interest Payment</span>
                    <span className="font-bold">{formatCurrency(result.annualInterestPayment)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Total Interest Payment</span>
                    <span className="font-bold">{formatCurrency(result.totalInterestPayment)}</span>
                  </div>
                </div>
              </div>

              <button className="w-full bg-orange-400 text-white py-2 rounded-lg mt-4 hover:bg-orange-500 transition-colors text-sm">
                Apply for Loan →
              </button>
            </div>
          </div>

          {showShareToast && (
            <div className="fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded shadow-lg">
              Link copied!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EligibilityCalculator;